import type {
  AppEntitlement,
  AppItem,
  EntitlementResolution,
} from "@/types/app";
import type { DecodedJwtPayload } from "@/types/token";
import {
  extractGroups,
  extractRoles,
  toStringArray,
} from "@/utils/jwt";

type Context = {
  roles: string[];
  groups: string[];
  claims: DecodedJwtPayload | null;
};

/**
 * Replicates the visibility rules used by myapps.microsoft.com. See
 * README § "MyApps-style visibility rules" for the full truth table.
 */
function evaluate(app: AppItem, ctx: Context): AppEntitlement {
  const reasons: string[] = [];
  const visibleToUsers = app.visibleToUsers !== false;
  const assignmentRequired = app.assignmentRequired === true;

  if (app.status === "restricted") {
    reasons.push("App marked as restricted by administrator");
    return { appId: app.id, app, visible: false, reasons };
  }

  if (!visibleToUsers) {
    reasons.push('Hidden by admin – "Visible to users?" = No');
    return { appId: app.id, app, visible: false, reasons };
  }

  if (app.visible === false) {
    reasons.push("App globally hidden (visible=false)");
    return { appId: app.id, app, visible: false, reasons };
  }

  let allowed = true;
  let matchedEntitlementSignal = false;

  if (app.requiredRoles?.length) {
    const missing = app.requiredRoles.filter((r) => !ctx.roles.includes(r));
    if (missing.length === app.requiredRoles.length) {
      allowed = false;
      reasons.push(
        `Missing required role(s): ${app.requiredRoles.join(", ")}`,
      );
    } else {
      const matched = app.requiredRoles.filter((r) => ctx.roles.includes(r));
      matchedEntitlementSignal = true;
      reasons.push(`Has required role(s): ${matched.join(", ")}`);
    }
  }

  if (app.requiredGroups?.length) {
    const missing = app.requiredGroups.filter((g) => !ctx.groups.includes(g));
    if (missing.length === app.requiredGroups.length) {
      allowed = false;
      reasons.push(
        `Missing required group(s): ${app.requiredGroups.join(", ")}`,
      );
    } else {
      const matched = app.requiredGroups.filter((g) => ctx.groups.includes(g));
      matchedEntitlementSignal = true;
      reasons.push(`Has required group(s): ${matched.join(", ")}`);
    }
  }

  if (app.requiredClaims && Object.keys(app.requiredClaims).length > 0) {
    for (const [key, expected] of Object.entries(app.requiredClaims)) {
      const actual = toStringArray(ctx.claims?.[key]);
      const expectedArr = Array.isArray(expected) ? expected : [expected];
      const hits = expectedArr.filter((e) => actual.includes(e));
      if (hits.length === 0) {
        allowed = false;
        reasons.push(
          `Missing required claim "${key}" = ${expectedArr.join(" | ")}`,
        );
      } else {
        matchedEntitlementSignal = true;
        reasons.push(
          `Has required claim "${key}" (matched: ${hits.join(", ")})`,
        );
      }
    }
  }

  if (
    !app.requiredRoles?.length &&
    !app.requiredGroups?.length &&
    !app.requiredClaims
  ) {
    reasons.push("App has no claim-based gates – visible to every signed-in user");
  }

  if (allowed && assignmentRequired) {
    if (app.assignedToMe === true) {
      reasons.push(
        "Assignment required and user is assigned to the Enterprise Application.",
      );
    } else if (matchedEntitlementSignal) {
      reasons.push(
        "Assignment required; matched via role / group / claim gate.",
      );
    } else {
      allowed = false;
      reasons.push(
        'Hidden – "Assignment required?" = Yes and the user is not assigned to this application.',
      );
    }
  }

  if (allowed && app.status === "coming_soon") {
    reasons.push("Tile visible, launch disabled (coming soon).");
  }

  return { appId: app.id, app, visible: allowed, reasons };
}

export function resolveEntitlements(
  catalog: AppItem[],
  claims: DecodedJwtPayload | null,
): EntitlementResolution {
  const ctx: Context = {
    roles: extractRoles(claims),
    groups: extractGroups(claims),
    claims,
  };

  const byId: Record<string, AppEntitlement> = {};
  const allowed: AppEntitlement[] = [];
  const denied: AppEntitlement[] = [];

  for (const app of catalog) {
    const res = evaluate(app, ctx);
    byId[app.id] = res;
    (res.visible ? allowed : denied).push(res);
  }

  return { allowed, denied, byId };
}
